import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { openPrivacy, whatsappUrl, WHATSAPP_NUMBER } from "@/lib/site";

const EASE = [0.16, 1, 0.3, 1];
export const NUMERO_LEGGIBILE = `+${WHATSAPP_NUMBER.slice(0, 2)} ${WHATSAPP_NUMBER.slice(2, 5)} ${WHATSAPP_NUMBER.slice(5, 8)} ${WHATSAPP_NUMBER.slice(8)}`;
const PHONE_RE = /^\+?[0-9\s.-]{8,18}$/;

const fieldCls =
  "min-h-[52px] w-full rounded-full border border-[#4D2C20] bg-[#16110E] px-6 py-4 text-sm text-[#FAF7F2] placeholder-[#A89F91] outline-none transition-colors duration-300 focus:border-[#E05A2C]";

/**
 * Modulo "stile Hero" riusabile.
 * fields: [{ key, type: "select"|"text"|"tel"|"url", placeholder, options?, validate?(value) -> errore|"" }]
 */
export function CheckForm({ id, testid, kicker, fields, endpoint, buttonLabel, promise, consentText, secondaryLink }) {
  const [values, setValues] = useState(() => Object.fromEntries(fields.map((f) => [f.key, ""])));
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const fail = (msg) => {
    setError(msg);
    toast.error(msg);
  };
  const set = (key) => (e) => {
    setValues((v) => ({ ...v, [key]: e.target.value }));
    setError("");
  };

  const submit = async () => {
    for (const f of fields) {
      const msg = f.validate(values[f.key].trim());
      if (msg) return fail(msg);
    }
    if (!consent) return fail("Per inviare serve spuntare la casella del consenso privacy.");
    setError("");
    setSending(true);
    try {
      const body = Object.fromEntries(fields.map((f) => [f.key, values[f.key].trim()]));
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...body, consent }),
      });
      if (!res.ok) throw new Error();
      setDone(true);
    } catch {
      fail("Qualcosa non ha funzionato. Riprova tra poco, o scrivici direttamente su WhatsApp col pulsante verde.");
    } finally {
      setSending(false);
    }
  };

  const renderField = (f, extra = "") =>
    f.type === "select" ? (
      <select key={f.key} data-testid={`${testid}-${f.key}`} value={values[f.key]} onChange={set(f.key)} className={`${fieldCls} appearance-none ${extra}`}>
        <option value="" className="bg-[#16110E]">{f.placeholder}</option>
        {f.options.map((o) => (
          <option key={o} value={o} className="bg-[#16110E]">{o}</option>
        ))}
      </select>
    ) : (
      <input
        key={f.key}
        data-testid={`${testid}-${f.key}`}
        type={f.type}
        value={values[f.key]}
        onChange={set(f.key)}
        onKeyDown={(e) => e.key === "Enter" && submit()}
        placeholder={f.placeholder}
        className={`${fieldCls} ${extra}`}
      />
    );

  const [last, ...firsts] = [...fields].reverse();
  const top = firsts.reverse();

  return (
    <div id={id} data-testid={testid} className="mt-8 scroll-mt-28">
      {done ? (
        <div data-testid={`${testid}-success`} className="border border-[#383029] p-6">
          <p className="font-heading text-lg font-black uppercase tracking-tight text-[#FAF7F2]">Ricevuto!</p>
          <p className="mt-2 text-sm leading-relaxed text-[#C4BBB0] sm:text-base">
            Entro 48 ore ti scriviamo su WhatsApp dal numero <span className="font-medium text-[#FAF7F2]">{NUMERO_LEGGIBILE}</span>.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="section-kicker">{kicker}</p>
          {top.length > 0 && <div className={`grid gap-3 ${top.length > 1 ? "sm:grid-cols-2" : ""}`}>{top.map((f) => renderField(f))}</div>}
          <div className="flex flex-col gap-3 sm:flex-row">
            {renderField(last, "flex-1")}
            <button
              data-testid={`${testid}-submit`}
              onClick={submit}
              disabled={sending}
              className="btn-glow min-h-[52px] shrink-0 rounded-full bg-[#E05A2C] px-8 py-4 text-sm font-medium text-white transition-colors duration-300 hover:bg-[#F26B3A] active:scale-[0.98] disabled:opacity-50"
            >
              {sending ? "Invio…" : buttonLabel}
            </button>
          </div>
          <label className="flex max-w-lg cursor-pointer items-start gap-3 text-sm leading-relaxed text-[#A89F91]">
            <input data-testid={`${testid}-consent`} type="checkbox" checked={consent} onChange={(e) => { setConsent(e.target.checked); setError(""); }} className="mt-1 h-4 w-4 shrink-0 accent-[#E05A2C]" />
            <span>
              Ho letto l'{" "}
              <button type="button" onClick={openPrivacy} className="underline decoration-[#383029] underline-offset-2 transition-colors duration-300 hover:text-[#E05A2C]">informativa privacy</button>{" "}
              {consentText}
            </span>
          </label>
          {error && (
            <p data-testid={`${testid}-error`} role="alert" className="border-l-2 border-[#E05A2C] pl-4 text-sm leading-relaxed text-[#E05A2C]">{error}</p>
          )}
          <p data-testid={`${testid}-promise`} className="text-sm leading-relaxed text-[#C4BBB0]">{promise}</p>
          {secondaryLink && (
            <Link to={secondaryLink.to} data-testid={`${testid}-secondary-link`} className="inline-flex min-h-[44px] items-center self-start text-sm text-[#A89F91] underline decoration-[#383029] underline-offset-4 transition-colors duration-300 hover:text-[#E05A2C]">
              {secondaryLink.label} →
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

export const validators = {
  required: (msg) => (v) => (v ? "" : msg),
  minLen: (n, msg) => (v) => (v.length >= n ? "" : msg),
  phone: (v) => (PHONE_RE.test(v) ? "" : "Inserisci il tuo numero WhatsApp, con prefisso: per esempio +39 347 1234567."),
  link: (v) => (v && v.includes(".") && !v.includes(" ") ? "" : "Incolla il link del tuo annuncio: lo trovi nella barra in alto quando apri la tua pagina su Booking o Airbnb."),
};

const RISPOSTE = ["Sì", "No", "Non lo so"];

const STATI = [
  { id: "aperta", label: "È già aperta e operativa", breve: "già aperta" },
  { id: "apertura", label: "È in fase di apertura", breve: "in fase di apertura" },
];

/**
 * Quiz Sì / No / Non lo so, stile calcolatore. Prima domanda: stato della struttura.
 * resultKo: (n) -> testo; waMessage: (righe, stato) -> testo WhatsApp
 */
export function CheckQuiz({ testid, kicker, title, questions, resultOk, resultKo, waLabel, waMessage, extraOk }) {
  const [stato, setStato] = useState(null);
  const [answers, setAnswers] = useState([]);
  const step = answers.length;
  const finished = stato && step === questions.length;
  const daVerificare = answers.filter((a) => a !== "Sì").length;

  const righe = questions.map((d, i) => `${i + 1}) ${d} ${answers[i]}`).join("; ");
  const reset = () => {
    setStato(null);
    setAnswers([]);
  };

  return (
    <div data-testid={testid} className="mt-10 border border-[#4D2C20] bg-[#2B1B15] p-6 sm:p-8">
      <p className="section-kicker">{kicker}</p>
      <p className="mt-3 font-heading text-lg font-black uppercase tracking-tight text-[#FAF7F2] sm:text-xl">{title}</p>
      <p className="mt-2 font-mono text-xs uppercase tracking-widest text-[#A89F91]">
        {finished ? "Risultato" : !stato ? "Prima di cominciare" : `Domanda ${step + 1} di ${questions.length}`}
      </p>
      <div className="mt-4 flex gap-1.5" aria-hidden>
        {questions.map((_, i) => (
          <span key={i} className={`h-1 flex-1 transition-colors duration-500 ${stato && i < step ? "bg-[#E05A2C]" : "bg-[#4D2C20]"}`} />
        ))}
      </div>

      <AnimatePresence mode="wait">
        {!stato ? (
          <motion.div key="stato" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.35, ease: EASE }} className="mt-6">
            <p data-testid="quiz-question" className="text-base font-light leading-relaxed text-[#FAF7F2] sm:text-lg">La tua struttura è già operativa o in fase di creazione?</p>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {STATI.map((s) => (
                <button
                  key={s.id}
                  data-testid={`quiz-stato-${s.id}`}
                  onClick={() => setStato(s)}
                  className="min-h-[52px] rounded-full border border-[#383029] bg-[#16110E] px-6 text-sm font-medium text-[#FAF7F2] transition-colors duration-300 hover:border-[#E05A2C] hover:bg-[#1C1815] active:scale-[0.98]"
                >
                  {s.label}
                </button>
              ))}
            </div>
          </motion.div>
        ) : !finished ? (
          <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.35, ease: EASE }} className="mt-6">
            <p data-testid="quiz-question" className="text-base font-light leading-relaxed text-[#FAF7F2] sm:text-lg">{questions[step]}</p>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              {RISPOSTE.map((r) => (
                <button
                  key={r}
                  data-testid={`quiz-answer-${r === "Sì" ? "si" : r === "No" ? "no" : "nonloso"}`}
                  onClick={() => setAnswers((prev) => [...prev, r])}
                  className="min-h-[52px] rounded-full border border-[#383029] bg-[#16110E] px-6 text-sm font-medium text-[#FAF7F2] transition-colors duration-300 hover:border-[#E05A2C] hover:bg-[#1C1815] active:scale-[0.98]"
                >
                  {r}
                </button>
              ))}
            </div>
          </motion.div>
        ) : (
          <motion.div key="result" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease: EASE }} className="mt-6">
            <div data-testid="quiz-result" className="bg-[#F7F4EC] px-6 py-7 text-[#12100E] sm:px-8">
              <p className="font-mono text-xs font-semibold uppercase tracking-widest text-[#686054]">Il tuo risultato · struttura {stato.breve}</p>
              <p className="mt-3 font-heading text-xl font-black uppercase leading-tight tracking-tight sm:text-2xl">
                {daVerificare === 0 ? resultOk : resultKo(daVerificare)}
              </p>
              {stato.id === "apertura" && (
                <p data-testid="quiz-nota-apertura" className="mt-3 text-sm text-[#52525B]">
                  Stai per aprire: i punti segnati in rosso sono quelli da impostare bene dal primo giorno, prima ancora di pubblicare l'annuncio.
                </p>
              )}
              <ul className="mt-5 space-y-2 border-t border-[#DCD7C9] pt-4">
                {questions.map((d, i) => (
                  <li key={d} className="flex items-baseline justify-between gap-4 text-sm">
                    <span className="text-[#52525B]">{d}</span>
                    <span className={`shrink-0 font-mono text-xs font-semibold uppercase tracking-widest ${answers[i] === "Sì" ? "text-[#15803D]" : "text-[#B91C1C]"}`}>{answers[i]}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <a
                href={whatsappUrl(waMessage(righe, stato.breve))}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="quiz-whatsapp-button"
                className="btn-glow inline-flex min-h-[52px] items-center rounded-full bg-[#E05A2C] px-8 py-4 text-sm font-medium text-white transition-colors duration-300 hover:bg-[#F26B3A] active:scale-[0.98]"
              >
                {waLabel}
              </a>
              {daVerificare === 0 && extraOk && (
                <button data-testid="quiz-call-button" onClick={extraOk.onClick} className="min-h-[44px] text-sm text-[#A89F91] underline decoration-[#383029] underline-offset-4 transition-colors duration-300 hover:text-[#E05A2C]">
                  {extraOk.label}
                </button>
              )}
              <button data-testid="quiz-reset-button" onClick={reset} className="min-h-[44px] text-sm text-[#A89F91] underline decoration-[#383029] underline-offset-4 transition-colors duration-300 hover:text-[#E05A2C]">
                Rifai il test
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
